import { useEffect, useMemo, useState } from "react";
import styles from "./StockHoldersOverTimeChart.module.scss";
import { getStockTopHolders } from "@/controllers/stock-holders";
import dayjs from "dayjs";
import { getChartDataNew } from "@/controllers/chart-data";
import { Stock } from "@/models/stock";
import TimeSeriesChart, { type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";

interface PropsTypes {
    stock: Stock;
    /** Holders the user added from the table, on the left scale with the rest. */
    extraSeries?: TimeSeries[];
}

/** Price and volume with the largest holders' positions over five years on the left scale. */
const StockHoldersOverTimeChart = ({ stock, extraSeries }: PropsTypes) => {
    const [bars, setBars] = useState<[number, number, number, number, number, number?][]>([]);
    const [holders, setHolders] = useState<Record<string, [number, number][]>>({});

    useEffect(() => {
        let alive = true;
        // The holders do not depend on the bars, so both requests start together; they used to go one
        // after the other (#1325). The holders are still drawn after the bars, as before: the chart opens
        // on "All", fitted to whatever it has when data first arrives, and that is the price history.
        const holdersRequest = getStockTopHolders(stock.stock_id, dayjs().subtract(5, "years").format("YYYY-MM-DD"));
        holdersRequest.catch(() => undefined);
        getChartDataNew(stock.tradingitem_id, "ohlcv", "2015-01-01", dayjs().format("YYYY-MM-DD"))
            .then((data) => {
                if (alive) setBars(data ?? []);
                return holdersRequest;
            })
            .then((data) => alive && setHolders(data ?? {}));
        return () => {
            alive = false;
        };
    }, [stock.stock_id]);

    const series = useMemo<TimeSeries[]>(
        () => [
            { id: "price", title: `${stock.ticker} Price in ${stock.currency}`, type: "line", data: bars.map((row) => [row[0], row[4]] as [number, number]), color: "#31ABA6", axis: "right", precision: 2 },
            { id: "volume", title: "Volume", type: "column", data: bars.map((row) => [row[0], row[row.length - 1] as number] as [number, number]), color: "dodgerblue", pane: "volume", format: "compact" },
            ...Object.entries(holders).map<TimeSeries>(([name, data]) => ({ id: name, title: name, type: "line", data, axis: "left", format: "compact", precision: 2 })),
            ...(extraSeries ?? []).filter((e) => !(e.id in holders)),
        ],
        [bars, holders, extraSeries, stock.ticker, stock.currency]
    );

    return (
        <div className={styles["chart-container"]}>
            <TimeSeriesChart series={series} paneHeights={{ volume: 0.35 }} initialRange="All" exportName={`${stock.ticker}-holders`} />
        </div>
    );
};

export default StockHoldersOverTimeChart;
